import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, Lock, Eye, EyeOff, AlertTriangle, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Switch } from '../components/ui/Switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Alert, AlertDescription } from '../components/ui/Alert';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Footer } from '../components/Footer';
import { sanitizeInput } from '../utils/security';
export function AdminLogin() {
  const {
    t
  } = useLanguage();
  const {
    adminLogin,
    isAdmin
  } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (isAdmin) navigate('/admin/dashboard');
  }, [isAdmin, navigate]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const cleanEmail = sanitizeInput(email).toLowerCase();
    if (!cleanEmail || !password) {
      setError(t('auth.error.required'));
      return;
    }
    setLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    const result = adminLogin(cleanEmail, password, rememberMe);
    if (result.success) {
      toast.success('Welcome, Admin!');
      navigate('/admin/dashboard');
    } else {
      setError(result.error || t('admin.error.credentials'));
    }
    setLoading(false);
  };
  return <div className="w-full min-h-screen flex flex-col" data-id="element-614">
      <div className="flex-1 flex items-center justify-center py-12 px-4" data-id="element-615">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-md" data-id="element-616">
          <Card className="shadow-lg border-2 border-blue-600/20" data-id="element-617">
            <CardHeader className="text-center pb-2" data-id="element-618">
              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              delay: 0.2
            }} className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mb-3 shadow-lg shadow-blue-600/20" data-id="element-619">
                <Shield className="h-7 w-7 text-white" data-id="element-620" />
              </motion.div>
              <CardTitle className="text-xl" data-id="element-621">
                {t('admin.login.title')}
              </CardTitle>
              <CardDescription data-id="element-622">Secure admin access only</CardDescription>
            </CardHeader>

            <CardContent className="pt-4" data-id="element-623">
              {error && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} data-id="element-624">
                  <Alert variant="destructive" className="mb-4" data-id="element-625">
                    <AlertTriangle className="h-4 w-4" data-id="element-626" />
                    <AlertDescription data-id="element-627">{error}</AlertDescription>
                  </Alert>
                </motion.div>}

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-628">
                <div className="space-y-2" data-id="element-629">
                  <Label className="flex items-center gap-2" data-id="element-630">
                    <Mail className="h-3.5 w-3.5" data-id="element-631" /> {t('admin.email')}
                  </Label>
                  <Input type="email" placeholder="admin@example.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" data-id="element-632" />
                </div>

                <div className="space-y-2" data-id="element-633">
                  <Label className="flex items-center gap-2" data-id="element-634">
                    <Lock className="h-3.5 w-3.5" data-id="element-635" /> {t('admin.password')}
                  </Label>
                  <div className="relative" data-id="element-636">
                    <Input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className="pr-10" autoComplete="current-password" data-id="element-637" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-id="element-638">
                      {showPassword ? <EyeOff className="h-4 w-4" data-id="element-639" /> : <Eye className="h-4 w-4" data-id="element-640" />}
                    </button>
                  </div>
                </div>

                {/* Remember Me */}
                <div className="flex items-center justify-between" data-id="element-641">
                  <div className="flex items-center gap-2" data-id="element-642">
                    <Switch checked={rememberMe} onCheckedChange={setRememberMe} id="remember-admin" data-id="element-643" />
                    <Label htmlFor="remember-admin" className="text-sm cursor-pointer flex items-center gap-1.5" data-id="element-644">
                      <KeyRound className="h-3 w-3" data-id="element-645" />
                      {t('auth.remember')}
                    </Label>
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2" data-id="element-646">
                  {loading ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-647" /> : <Shield className="h-4 w-4" data-id="element-648" />}
                  {t('admin.login.btn')}
                </Button>
              </form>

              <div className="mt-6 p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground text-center space-y-1" data-id="element-649">
                <p className="flex items-center justify-center gap-1" data-id="element-650">
                  <Lock className="h-3 w-3" data-id="element-651" />
                  Protected with brute-force detection
                </p>
                <p data-id="element-652">5 failed attempts = 5 minute lockout</p>
                {rememberMe && <p className="text-blue-500 flex items-center justify-center gap-1 mt-1" data-id="element-653">
                    <KeyRound className="h-3 w-3" data-id="element-654" />
                    Login will be saved for next visit
                  </p>}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
      <Footer data-id="element-655" />
    </div>;
}